import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Phone, AddressBook, Target, ClockCounterClockwise,
  ChartBar, Gear, SignOut, List, Queue
} from "@phosphor-icons/react";
import "./AppLayout.css";

const NAV = [
  { path: "/app/phone",     Icon: Phone,                 label: "Phone" },
  { path: "/app/contacts",  Icon: AddressBook,           label: "Contacts" },
  { path: "/app/leads",     Icon: Target,                label: "Leads" },
  { path: "/app/history",   Icon: ClockCounterClockwise, label: "History" },
  { path: "/app/queue",     Icon: Queue,                 label: "Queue" },
  { path: "/app/dashboard", Icon: ChartBar,              label: "Dashboard" },
];

export default function AppLayout({ dialerState }) {
  const { user, signout, branding } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("lv_sidebar") === "1");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("lv_sidebar", collapsed ? "1" : "0");
  }, [collapsed]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignout = () => { signout(); navigate("/signin"); };


  const logoCollapsed = branding?.logo_collapsed;
  const logoFull = branding?.logo_full;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${collapsed ? "collapsed" : "expanded"}`}>
        {/* Logo */}
        <div className="sidebar-logo-area">
          {collapsed ? (
            logoCollapsed
              ? <img src={logoCollapsed} alt="logo" className="logo-img-collapsed"/>
              : <div className="logo-mark-box"><span className="logo-mark">L</span></div>
          ) : (
            logoFull
              ? <img src={logoFull} alt="logo" className="logo-img-full"/>
              : <div className="logo-full-default"><span className="logo-mark">L</span><span className="logo-text">eovoid</span></div>
          )}
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {NAV.map(({ path, Icon, label }) => (
            <NavLink key={path} to={path}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title={collapsed ? label : undefined}
              style={{ position: "relative" }}>
              <Icon size={20} weight="regular" className="nav-icon-svg"/>
              {!collapsed && <span className="nav-label-text">{label}</span>}
              {label === "Queue" && dialerState?.queueCalls?.length > 0 && (
                <span style={{
                  position: "absolute", top: 6, right: collapsed ? 6 : 10,
                  background: "var(--color-danger)", color: "white",
                  borderRadius: "50%", width: 16, height: 16,
                  fontSize: 10, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {dialerState.queueCalls.length}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="sidebar-bottom">
          {/* Settings - admin only */}
          {user?.role === "admin" && (
          <NavLink to="/app/settings"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            title={collapsed ? "Settings" : undefined}>
            <Gear size={20} weight="regular" className="nav-icon-svg"/>
            {!collapsed && <span className="nav-label-text">Settings</span>}
          </NavLink>
          )}

          {/* Collapse toggle */}
          <button className="nav-item collapse-btn" onClick={() => setCollapsed(c => !c)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <List size={20} className="nav-icon-svg"/>
            {!collapsed && <span className="nav-label-text">Collapse</span>}
          </button>

          {/* Avatar */}
          <div className="user-avatar-wrap" ref={userMenuRef}>
            <div className={`user-avatar ${collapsed ? "collapsed" : ""}`}
              onClick={() => setShowUserMenu(s => !s)}>
              <div className="avatar-circle">
                {user?.avatar_url
                  ? <img src={user.avatar_url} alt="avatar" style={{ width:"100%", height:"100%", borderRadius:"50%", objectFit:"cover" }}/>
                  : <span>{user?.avatar_initials || "?"}</span>
                }
              </div>
              {!collapsed && (
                <div className="user-avatar-info">
                  <span className="user-avatar-name">{user?.first_name} {user?.last_name}</span>
                  <span className="user-avatar-email">{user?.email}</span>
                </div>
              )}

            </div>

            {showUserMenu && (
              <div className="user-menu">
                <div className="user-menu-name">{user?.first_name} {user?.last_name}</div>
                <div className="user-menu-email">{user?.email}</div>
                <div className="divider" />
                <button onClick={handleSignout} className="user-menu-item danger">
                  <SignOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="app-main">
        <Outlet context={{ dialerState }} />
      </main>

      {/* Queue call notification */}
      {dialerState?.pendingQueueCall && !dialerState?.isInCall && (
        <div className="incoming-overlay" style={{ top: dialerState?.incomingCall ? 140 : 20 }}>
          <div className="incoming-card">
            <div className="incoming-ring">
              <Phone size={24} weight="fill" color="var(--color-orange)"/>
            </div>
            <div className="incoming-info">
              <div className="incoming-label" style={{ color: "var(--color-orange)" }}>
                Queue Call {dialerState.queueCalls?.length > 1 ? `· ${dialerState.queueCalls.length} waiting` : ""}
              </div>
              <div className="incoming-number">{dialerState.pendingQueueCall.from_number || "Unknown"}</div>
            </div>
            <div className="incoming-actions">
              <button className="btn btn-primary" onClick={() => dialerState.dequeueCall(dialerState.pendingQueueCall.call_sid, dialerState.pendingQueueCall.queue_sid)}>Answer</button>
              <button className="btn btn-danger" onClick={() => dialerState.declineQueueCall(dialerState.pendingQueueCall.call_sid, dialerState.pendingQueueCall.queue_sid)}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Incoming call overlay */}
      {dialerState?.incomingCall && (
        <div className="incoming-overlay">
          <div className="incoming-card">
            <div className="incoming-ring"><Phone size={24} weight="fill" color="var(--color-teal)"/></div>
            <div className="incoming-info">
              <div className="incoming-label">Incoming Call</div>
              <div className="incoming-number">{dialerState.incomingCall.parameters?.From || "Unknown"}</div>
            </div>
            <div className="incoming-actions">
              <button className="btn btn-primary" onClick={dialerState.accept}>Accept</button>
              <button className="btn btn-danger" onClick={dialerState.reject}>Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
